import { Button, Spinner, Textfield, Tooltip } from '@bi-digital/react';
import { ThumbDownFilledIcon, ThumbDownIcon, ThumbUpFilledIcon, ThumbUpIcon } from '@bi-digital/icons';
import { Fragment, useRef, useState } from 'react';
import { type SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useTurnstile } from '@bino/utils/hooks';
import { useFeedbackForm } from '../hooks/useFeedbackForm';
import styles from './FeedbackContent.module.css';
import type { FeedbackVariant, Inputs } from '../types';

const getViewportSize = () => `${window.innerWidth}x${window.innerHeight}`;

type FeedbackContentProps = {
    source: FeedbackVariant;
    // Lets the parent react to a successful submit (the footer dialog uses it to auto-close).
    onSubmitted?: () => void;
    turnstileSiteKey: string;
};

/**
 * The feedback form itself - thumbs, optional comment and the submit logic.
 * Shared as-is by both variants; only the surrounding chrome differs.
 */
export const FeedbackContent = ({ source, onSubmitted, turnstileSiteKey }: FeedbackContentProps) => {
    const isFooter = source === 'Footer';
    const { t } = useTranslation(['feedback']);
    const { postFeedback } = useFeedbackForm();
    const {
        containerRef: turnstileRef,
        token: turnstileToken,
        isLoading: turnstileLoading,
        needsInteraction: turnstileNeedsInteraction,
        consumeToken: consumeTurnstileToken,
        widget: turnstileWidget,
    } = useTurnstile(turnstileSiteKey);

    const [status, setStatus] = useState<'form' | 'success' | 'error'>('form');
    const [loading, setLoading] = useState(false);

    const guidRef = useRef<string>(crypto.randomUUID());
    const isSubmittingRef = useRef(false);

    const {
        register,
        handleSubmit,
        control,
        setFocus,
        formState: { errors },
    } = useForm<Inputs>({
        defaultValues: { feedbackComment: '' },
    });
    const feedbackValue = useWatch({ control, name: 'feedbackValue' });

    const onSubmit: SubmitHandler<Inputs> = async (values) => {
        // Ref (not state) guards double submits synchronously; setLoading only drives the button spinner.
        if (isSubmittingRef.current || !turnstileToken) return;
        isSubmittingRef.current = true;

        setLoading(true);

        const { success } = await postFeedback({
            ...values,
            feedbackRoute: window.location.pathname,
            source,
            guid: guidRef.current,
            viewportSize: getViewportSize(),
            turnstileToken: consumeTurnstileToken() ?? '',
        });

        setStatus(success ? 'success' : 'error');
        if (success) {
            onSubmitted?.();
        }

        setLoading(false);
        isSubmittingRef.current = false;
    };

    const showComment = isFooter || !!feedbackValue;
    const commentPrompt = feedbackValue === 'no' ? t('block.negativeLabel') : t('block.positiveLabel');
    const commentPlaceholder = t(isFooter ? 'footer.placeholder' : 'block.placeholder');

    const errorId = `${source}-feedback-error`;

    return (
        <form className={styles.form} onSubmit={(e) => handleSubmit(onSubmit, () => setFocus('feedbackValue'))(e)}>
            {status === 'success' && <div className={styles.alert}>{t('confirmation')}</div>}

            {status === 'error' && (
                <>
                    <div className={styles.alert}>{t('error')}</div>
                    <Button type='button' variant='flat' onClick={() => setStatus('form')}>
                        {t('tryAgain')}
                    </Button>
                </>
            )}

            {status === 'form' && (
                <>
                    <div
                        className={styles.radiogroup}
                        role='radiogroup'
                        aria-label={isFooter ? t('footer.title') : t('block.title')}
                        aria-invalid={!!errors.feedbackValue}
                        aria-describedby={errors.feedbackValue ? errorId : undefined}
                    >
                        {(['yes', 'no'] as const).map((value) => {
                            const isSelected = feedbackValue === value;
                            const Icon =
                                value === 'yes'
                                    ? isSelected
                                        ? ThumbUpFilledIcon
                                        : ThumbUpIcon
                                    : isSelected
                                      ? ThumbDownFilledIcon
                                      : ThumbDownIcon;
                            const tooltip = isFooter ? (value === 'yes' ? t('like') : t('dislike')) : t(value);
                            return (
                                <Fragment key={value}>
                                    <input
                                        type='radio'
                                        {...register('feedbackValue', {
                                            required: true,
                                        })}
                                        value={value}
                                        id={`${source}-${value}`}
                                        className={styles.hiddenRadio}
                                    />
                                    <Tooltip content={tooltip} placement='bottom'>
                                        <label
                                            className={styles.radioLabel}
                                            htmlFor={`${source}-${value}`}
                                            aria-label={t(value)}
                                        >
                                            <Icon
                                                size='large'
                                                aria-hidden={'true'}
                                                className={isSelected ? styles.thumbActive : styles.thumbInactive}
                                            />
                                        </label>
                                    </Tooltip>
                                </Fragment>
                            );
                        })}
                    </div>

                    {showComment && (
                        <>
                            {feedbackValue && !isFooter && <p className={styles.feedbackLabel}>{commentPrompt}</p>}
                            <Textfield
                                className={styles.feedbackInput}
                                {...register('feedbackComment', {
                                    shouldUnregister: false,
                                })}
                                aria-label={feedbackValue ? commentPrompt : t('footer.title')}
                                placeholder={commentPlaceholder}
                                multiline
                            />
                        </>
                    )}

                    {errors.feedbackValue && (
                        <div id={errorId} role='alert' className={styles.alert}>
                            {t('submitHint')}
                        </div>
                    )}

                    {turnstileLoading && !turnstileNeedsInteraction && (
                        <p className={styles.turnstileLoading}>
                            <Spinner size='small' aria-hidden='true' />
                            {t('turnstileLoading')}
                        </p>
                    )}

                    <Button type='submit' variant='flat' submitting={loading} disabled={!turnstileToken}>
                        {t('submit')}
                    </Button>
                </>
            )}

            <div ref={turnstileRef} className={styles.turnstile} hidden={status !== 'form'}>
                {turnstileWidget}
            </div>
        </form>
    );
};
